import React, { forwardRef, useEffect, useImperativeHandle, useMemo, useRef, useState } from 'react'
import Select from 'react-select';
import JoditEditor from 'jodit-react';
import CustomColumn from '../../../../../customControl/CustomColumn';
import { getHTMLContentCharacterLength, onlyAlphabets } from '../../../../commonClass/CommonValidation';
import { SelectDropDownCheckboxInputOption } from '../../../../commonClass/CommonFunctions';


const BasicInformationSection = forwardRef(({ BasicInformationForm, setBasicInformationForm, formErrors, setFormErrors, dropdownData, disabled, setEditDisabled, setPointAddressForm }, ref) => {

    //#region richtext box ref
    const longDescriptionsRef = useRef(null);
    const highlightsRef = useRef(null);
    //#endregion

    //#region richtext box config
    const config = useMemo(() => ({
        readonly: false, // all options from https://xdsoft.net/jodit/docs/,
        // placeholder: 'Start typings...',
        width: '100%',
        statusbar: false,
        disablePlugins: ["source"],
        removeButtons: ["about", "image", "link", "select", "video"],
        toolbarAdaptive: false,
        defaultMode: "1",
        disabled: disabled ? true : false,
        controls: {
            font: {
                list: {
                    'sans-serif': 'San Serif',
                    'Open Sans': ' Open Sans'
                }
            }
        },
    }), [disabled]);
    //#endregion

    //#region get category list based on selected collection
    const categoryList = useMemo(() => {
        return dropdownData?.collectionWiseCategory?.find((prev) => prev?.value === BasicInformationForm?.CollectionId)?.CategoryList || [];
    }, [dropdownData?.collectionWiseCategory, BasicInformationForm?.CollectionId])
    //#endregion

    //#region get activity list based on selected category
    const activityList = useMemo(() => {
        return dropdownData?.activityList?.filter((prev) => prev?.CategoryIds?.includes(BasicInformationForm?.CategoryId)) || [];
    }, [dropdownData?.activityList, BasicInformationForm?.CategoryId])
    //#endregion

    //#region on change function
    const handleOnChange = (name, value) => {
        setEditDisabled(false);
        setFormErrors(null);
        if (name === "CollectionId") {
            setBasicInformationForm((prev) => ({ ...prev, CollectionId: value, CategoryId: 0 }));
        }
        else if (name === "CategoryId") {
            setBasicInformationForm((prev) => ({ ...prev, CategoryId: value, ActivityId: 0 }));
        }
        else if (name === "ActivityId") {
            setBasicInformationForm((prev) => ({ ...prev, ActivityId: value }));
            setPointAddressForm((prev) => ({ ...prev, ActivityBasicInfoAddress: [...prev?.ActivityBasicInfoAddress?.map((obj) => ({ ...obj, StateId: 0, LocationId: 0 }))] }))
        }
        else {
            setBasicInformationForm((prev) => ({ ...prev, [name]: value }));
        }
    }
    //#endregion

    //#region validations
    const ValidateBasicInformationForm = () => {
        let error = {};

        if (BasicInformationForm?.SupplierId <= 0 || !BasicInformationForm?.SupplierId) {
            error.SupplierId = "Cannot leave blank, input is mandatory.";
        }
        if (BasicInformationForm?.CollectionId <= 0 || !BasicInformationForm?.CollectionId) {
            error.CollectionId = "Kindly select the Collection Name.";
        }
        if (BasicInformationForm?.CategoryId <= 0 || !BasicInformationForm?.CategoryId) {
            error.CategoryId = "Kindly select the category Name.";
        }
        if (BasicInformationForm?.ActivityId <= 0 || !BasicInformationForm?.ActivityId) {
            error.ActivityId = "Kindly select the activity Name";
        }
        if (BasicInformationForm?.ShortDescriptions?.trim()?.length <= 0 || !BasicInformationForm?.ShortDescriptions) {
            error.ShortDescriptions = "Cannot leave blank,";
        }
        if (getHTMLContentCharacterLength(BasicInformationForm?.LongDescriptions) <= 0) {
            error.LongDescriptions = "Cannot leave blank,";
        }
        if (getHTMLContentCharacterLength(BasicInformationForm?.LongDescriptions) > 0 && (getHTMLContentCharacterLength(BasicInformationForm?.LongDescriptions) < 25 || getHTMLContentCharacterLength(BasicInformationForm?.LongDescriptions) > 500)) {
            // error.LongDescriptions = "Please enter between 25 and 500 characters.";
            error.LongDescriptions = "";
        }
        if (getHTMLContentCharacterLength(BasicInformationForm?.Highlights) > 250) {
            // error.Highlights = "Maximum 250 characters allowed.";
            error.Highlights = "";
        }

        return error;
    }
    //#endregion


    // Expose the childFunction to the parent's ref
    useImperativeHandle(ref, () => ({
        canValidate: true,
        ValidateBasicInformationForm: ValidateBasicInformationForm
    }));


    return (
        <>
            <div className="container-fluid form_border my-2 py-3">
                <h5 className="mb-3">
                    <strong>Basic Information</strong>
                </h5>

                <div className="row">
                    <CustomColumn labelText='Select Supplier' labelId='SupplierId' isRequired >
                        <Select
                            className="dropdown select_dropdown"
                            inputId='SupplierId'
                            options={dropdownData?.supplierList}
                            value={dropdownData?.supplierList?.find((prev) => prev?.value === BasicInformationForm?.SupplierId) || 0}
                            onChange={(e) => handleOnChange("SupplierId", e ? e?.value : 0)}
                            isSearchable
                            isDisabled={disabled ? true : false}
                            placeholder='Select Supplier'
                        />
                        {formErrors?.SupplierId && <div className="text-danger text-start fs_14">{formErrors?.SupplierId}</div>}
                    </CustomColumn>

                    <CustomColumn labelText='Collection' labelId='CollectionId' isRequired >
                        <Select
                            className="dropdown select_dropdown"
                            inputId='CollectionId'
                            options={dropdownData?.collectionWiseCategory}
                            value={dropdownData?.collectionWiseCategory?.find((prev) => prev?.value === BasicInformationForm?.CollectionId) || 0}
                            onChange={(e) => handleOnChange("CollectionId", e ? e?.value : 0)}
                            isSearchable
                            isDisabled={disabled ? true : false}
                            placeholder='Collection'
                        />
                        {formErrors?.CollectionId && <div className="text-danger text-start fs_14">{formErrors?.CollectionId}</div>}
                    </CustomColumn>

                    <CustomColumn labelText='Category' labelId='CategoryId' isRequired >
                        <Select
                            className="dropdown select_dropdown"
                            inputId='CategoryId'
                            options={categoryList}
                            value={categoryList?.find((prev) => prev?.value === BasicInformationForm?.CategoryId) || 0}
                            onChange={(e) => handleOnChange("CategoryId", e ? e?.value : 0)}
                            isSearchable
                            isDisabled={disabled ? true : false}
                            placeholder='Category'
                        />
                        {formErrors?.CategoryId && <div className="text-danger text-start fs_14">{formErrors?.CategoryId}</div>}
                    </CustomColumn>

                    <CustomColumn labelText='Name Your Activity' labelId='ActivityId' isRequired >
                        <Select
                            className="dropdown select_dropdown"
                            inputId='ActivityId'
                            options={activityList}
                            value={activityList?.find((prev) => prev?.value === BasicInformationForm?.ActivityId) || 0}
                            onChange={(e) => handleOnChange("ActivityId", e ? e?.value : 0)}
                            isSearchable
                            isDisabled={disabled ? true : false}
                            placeholder='Activity /Experiences'
                        />
                        {formErrors?.ActivityId && <div className="text-danger text-start fs_14">{formErrors?.ActivityId}</div>}
                    </CustomColumn>

                    <CustomColumn labelText='One-line Descriptions' labelId='ShortDescriptions' isRequired >
                        <input
                            type="text"
                            className='form-control'
                            name="ShortDescriptions"
                            id="ShortDescriptions"
                            disabled={disabled ? true : false}
                            placeholder='Descriptions'
                            value={BasicInformationForm?.ShortDescriptions}
                            onChange={(e) =>
                                e?.target?.value?.length <= 200 &&
                                handleOnChange(e.target.name, onlyAlphabets(e?.target?.value))
                            }
                            maxLength={200}
                        />
                        {formErrors?.ShortDescriptions && <div className="text-danger text-start fs_14">{formErrors?.ShortDescriptions}</div>}
                    </CustomColumn>

                    <CustomColumn colNum={12} colLabel={2} colMDLabel={2} colChildren={10} colMDChildren={10} labelText='Detail Descriptions' labelId='LongDescriptions' isRequired >
                        <JoditEditor
                            ref={longDescriptionsRef}
                            tabIndex={1}
                            config={config}
                            value={BasicInformationForm?.LongDescriptions}
                            onBlur={newContent => handleOnChange("LongDescriptions", newContent)} // preferred to use only this option to update the content for performance reasons
                            onChange={newContent => { setFormErrors(null); }}
                        />
                        {formErrors?.LongDescriptions && <div className="text-danger text-start fs_14">{formErrors?.LongDescriptions}</div>}
                    </CustomColumn>

                    <CustomColumn colNum={12} colLabel={2} colMDLabel={2} colChildren={10} colMDChildren={10} labelText='Highlights' labelId='Highlights'>
                        <JoditEditor
                            ref={highlightsRef}
                            tabIndex={1}
                            config={config}
                            value={BasicInformationForm?.Highlights}
                            onBlur={newContent => handleOnChange("Highlights", newContent)} // preferred to use only this option to update the content for performance reasons
                            onChange={newContent => { setFormErrors(null); }}
                        />
                    </CustomColumn>

                </div>
            </div>
        </>
    )
});

export default BasicInformationSection